"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Loader2, ChevronDown, ChevronUp, Film, Image as ImageIcon, Box, Sparkles, Check, X } from "lucide-react";

type ActiveJob = {
  key: string;
  detail: string;
  type: "clip" | "image" | "now" | "3d" | "job";
  label: string;
  projectId: number | null;
  shotId?: number | null;
  user?: string | null;
  status?: string;
  resultId?: number | null;
};

const ICONS: Record<ActiveJob["type"], typeof Film> = {
  clip: Film,
  image: ImageIcon,
  now: Sparkles,
  "3d": Box,
  job: Loader2,
};

/**
 * Pannello flottante "Generazioni in corso": mostra in tempo reale tutti i job
 * attivi (clip/keyframe/Make it Now/3D), utile col batch parallelo. Si nasconde
 * da solo quando non c'è nulla in corso. Dati da /api/jobs (active[]).
 */
export default function ActiveWorkloads() {
  const pathname = usePathname();
  // Studio e Shot Studio hanno già un pannello generazioni interno → niente flottante lì
  const onStudio = (pathname?.endsWith("/studio") || pathname?.includes("/shot/")) ?? false;
  // Concept Board e Brainstorm: il flottante coprirebbe canvas/prompt bar →
  // la board ha il suo bottone "Generazioni", il brainstorm mostra il
  // progresso direttamente nel feed
  const onBoard =
    (pathname?.startsWith("/board") || pathname?.startsWith("/tool8")) ?? false;
  const [jobs, setJobs] = useState<ActiveJob[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (onStudio || onBoard) return;
    let alive = true;
    const load = () =>
      fetch("/api/jobs")
        .then((r) => r.json())
        .then((d) => {
          // `recent` = running + concluse di recente (restano cliccabili, non spariscono)
          if (alive) setJobs(Array.isArray(d?.recent) ? d.recent : Array.isArray(d?.active) ? d.active : []);
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [onStudio, onBoard]);

  const active = jobs.filter((j) => !dismissed.has(j.key)).slice(0, 10); // ultime 10
  const running = active.filter((j) => (j.status ?? "running") === "running").length;
  if (onStudio || onBoard || active.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-72 overflow-hidden rounded-lg border border-edge bg-panel-2 shadow-xl">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-panel"
        title="Generazioni in corso"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </span>
        <span className="text-xs font-semibold text-ink">Generazioni</span>
        <span className="rounded bg-accent/20 px-1.5 text-[10px] font-semibold text-accent">
          {running > 0 ? `${running} in corso` : `${active.length}`}
        </span>
        <span className="ml-auto text-ink-3">
          {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>
      {!collapsed && (
        <ul className="max-h-72 overflow-y-auto border-t border-edge">
          {active.map((j) => {
            const Icon = ICONS[j.type] ?? Loader2;
            const st = j.status ?? "running";
            const done = st === "done";
            const failed = st === "failed";
            // clip conclusa → link diretto allo Shot Studio sul take creato
            const href =
              done && j.type === "clip" && j.projectId && j.shotId
                ? `/projects/${j.projectId}/shot/${j.shotId}${j.resultId ? `?take=${j.resultId}` : ""}`
                : j.projectId
                  ? `/projects/${j.projectId}/script`
                  : null;
            const row = (
              <div className="flex items-start gap-2 px-3 py-2">
                <Icon size={13} className={`mt-0.5 shrink-0 ${failed ? "text-red-400" : "text-accent"}`} />
                {done ? (
                  <Check size={11} className="mt-0.5 shrink-0 text-emerald-400" />
                ) : failed ? (
                  <X size={11} className="mt-0.5 shrink-0 text-red-400" />
                ) : (
                  <Loader2 size={11} className="mt-0.5 shrink-0 animate-spin text-ink-3" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-ink">
                    {j.label}
                    {done && <span className="ml-1 text-[10px] text-emerald-400">✓ pronto</span>}
                    {failed && <span className="ml-1 text-[10px] text-red-400">fallita</span>}
                  </div>
                  <div className="truncate text-[10px] text-ink-3">
                    {j.user ? `${j.user.split("@")[0]} · ` : ""}
                    {done ? "apri nello Shot Studio" : failed ? j.detail || "errore" : j.detail}
                  </div>
                </div>
                {(done || failed) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setDismissed((s) => new Set(s).add(j.key));
                    }}
                    title="Rimuovi dalla lista"
                    className="mt-0.5 shrink-0 text-ink-3 hover:text-ink"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            );
            return (
              <li key={j.key} className="border-b border-edge/50 last:border-0 hover:bg-panel">
                {href ? <Link href={href}>{row}</Link> : row}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
